import AddIcon from '@mui/icons-material/Add';
import { Alert, Box } from '@mui/material';
import { Button, SearchBar } from '@peritasai/ui-components';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import type { CountSheetEntity } from '@peritasai/repository';
import { usePagedCountSheets } from '@peritasai/repository';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountSheetDrawer } from './CountSheetDrawer';
import { CountSheetsTable } from './CountSheetsTable';
import { DrawerMode } from '../../utils/drawer';

export const CountSheetsPage = () => {
  const { t } = useTranslation(['periverse']);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
  const [selectedCountSheet, setSelectedCountSheet] = useState<CountSheetEntity | null>(null);

  const { data, isLoading, isError, refetch } = usePagedCountSheets({
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    search: activeSearch,
  });

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    if (!value) {
      setActiveSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearch = (value: string) => {
    setActiveSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (row: CountSheetEntity) => {
    setSelectedCountSheet(row);
    setDrawerMode(DrawerMode.Edit);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setSelectedCountSheet(null);
    setDrawerMode(DrawerMode.Create);
    setDrawerOpen(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onClear={handleClear}
          placeholder={`${t('pages.entityManagement.searchPlaceholder', {
            entityName: t('pages.entityManagement.items.countSheets'),
          })}...`}
          width={{ xs: '100%', sm: '45%' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ flexShrink: 0 }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('pages.entityManagement.loadError')}
        </Alert>
      )}

      <CountSheetsTable
        rows={data?.data ?? []}
        isLoading={isLoading}
        hasSearch={activeSearch.length > 0}
        rowCount={data?.total ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onRowClick={handleRowClick}
      />

      <CountSheetDrawer
        open={drawerOpen}
        mode={drawerMode}
        selectedCountSheet={selectedCountSheet}
        onClose={() => setDrawerOpen(false)}
        onSuccess={() => { void refetch(); }}
      />
    </Box>
  );
};
